import React, { createContext, useContext, useState, ReactNode } from "react";

type DocumentUrl = {
  frontImagePath?: string;
  backImagePath?: string;
  frontFileName?: string;
  backFileName?: string;
};

interface DocumentScreeningOptions {
  [key: string]: string;
}

type DocumentData = {
  loanDetails: any;
  documentID: string;
  mediaID?: string;
  documentStatus: string;
  rejectionReason?: string;
  isAdditionalDocs: boolean;
  loanID: string;
  loanKycDetailsID: string;
  pageName: string;
  token: string;
  showDownloadDocument: boolean;
  documentName: string;
  documentType: string;
  documentUrl: DocumentUrl;
  documentPassword?: string;
  permissions?: any;
  isLenderDashboard?: boolean;
  coApplicantKYCDetailsID: string;
  isCoApplicant: boolean;
  uploadedFile?: string;
  showEditDocument?: boolean;
  allowDelete?: boolean;
  isBackImage?: boolean;
  tabPermissions?: any;
  docID?: string;
  documentScreeningOptions?: DocumentScreeningOptions;
  createdAt?: string;
  reloadParent?: boolean;
  displayNameInConfig: string;
  isCompact?: boolean;
};

type DocumentContextType = {
  document: DocumentData;
  updateDocument: (updates: Partial<DocumentData>) => void;
};

const DocumentContext = createContext<DocumentContextType | null>(null);

export const useDocument = (): DocumentContextType => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error("useDocument must be used within a DocumentProvider");
  }
  return context;
};

type DocumentProviderProps = {
  children: ReactNode;
};

export const DocumentProvider: React.FC<DocumentProviderProps> = ({
  children
}) => {
  const [document, setDocument] = useState<DocumentData>({
    loanDetails: {},
    documentID: "",
    mediaID: "",
    documentStatus: "",
    rejectionReason: "",
    isAdditionalDocs: false,
    loanID: "",
    loanKycDetailsID: "",
    pageName: "",
    token: "",
    showDownloadDocument: false,
    documentName: "",
    documentType: "",
    documentUrl: {
      frontImagePath: "",
      backImagePath: "",
      frontFileName: "",
      backFileName: ""
    },
    showEditDocument: false,
    allowDelete: false,
    documentPassword: "",
    permissions: {},
    coApplicantKYCDetailsID: "",
    isLenderDashboard: false,
    isCoApplicant: false,
    uploadedFile: "",
    isBackImage: false,
    tabPermissions: {},
    docID: "",
    documentScreeningOptions: {},
    createdAt: "",
    reloadParent: false,
    isCompact: false
  });

  const updateDocument = (updates: Partial<DocumentData>) => {
    setDocument((prev) => ({ ...prev, ...updates }));
  };

  return (
    <DocumentContext.Provider value={{ document, updateDocument }}>
      {children}
    </DocumentContext.Provider>
  );
};
